'use client';

import { useCallback, useState } from 'react';
import { PLATFORM, type Platform } from '@/constants';
import type { YMMData } from '@/interfaces/site/SiteYMMData';
import { getYMMCookies, setYMMCookies } from '@/packages/sxm/ymm';
import type { Capabilities } from '@/packages/sxm/ymm/types';

type PlatformCapabilities = Pick<Capabilities, 'siriusRadio' | 'siriusxmRadio' | 'xmRadio'>;

export function determinePlatformFromCapabilities(capabilities?: PlatformCapabilities): Platform {
    if (capabilities?.siriusRadio) {
        return PLATFORM.SIRIUS;
    }
    if (capabilities?.xmRadio) {
        return PLATFORM.XM;
    }
    return PLATFORM.SIRIUSXM;
}

function getInitialYmmData(): YMMData {
    const carData = getYMMCookies().sxm_radio.split('|');
    return carData[1]
        ? { year: carData[1], make: carData[2], model: carData[3], id: carData[4] }
        : { year: '', make: '', model: '', id: '' };
}

export function useVehiclePlatformSync() {
    const [ymmData, setYmmData] = useState<YMMData>(getInitialYmmData);

    const syncVehiclePlatform = useCallback(
        (nextYmm: YMMData) => {
            if (!nextYmm.id || nextYmm.id !== nextYmm.capabilities?.ymmComboId) {
                return;
            }

            setYMMCookies({
                sxm_radio: `YMM|${nextYmm.year}|${nextYmm.make}|${nextYmm.model}|${nextYmm.id}`,
                sxm_platform: determinePlatformFromCapabilities(nextYmm.capabilities),
                inft_ymm: nextYmm.capabilities.infotainment.join('|'),
                ymm_bar: '',
                roadblockbypass: '',
            });

            const selectionChanged =
                nextYmm.year !== ymmData.year ||
                nextYmm.make !== ymmData.make ||
                nextYmm.model !== ymmData.model ||
                nextYmm.id !== ymmData.id;

            if (selectionChanged) {
                setYmmData({
                    year: nextYmm.year,
                    make: nextYmm.make,
                    model: nextYmm.model,
                    id: nextYmm.id,
                });
                window.location.reload();
            }
        },
        [ymmData],
    );

    return { ymmData, syncVehiclePlatform };
}
